import {
  useCallback,
  useEffect,
  useState,
  type JSX,
  type ReactNode,
} from 'react';
import {
  apiBrandOffersAdminLogin,
  type BrandOffersAdminLoginRequest,
} from '@/services/slaydAPI/brandOffers';
import { BrandOffersAdminAuthContext } from './BrandOffersAdminAuthContext';
import { BRAND_OFFERS_ADMIN_AUTH_CHANGED_EVENT } from './BrandOffersAdminAuthContext.constants';
import type { BrandOffersAdminAuthContextType } from './BrandOffersAdminAuthContext.types';
import {
  clearBrandOffersAdminSession,
  notifyBrandOffersAdminAuthChanged,
  readBrandOffersAdminDisplayUser,
  readBrandOffersAdminToken,
  writeBrandOffersAdminSession,
} from './brandOffersAdminSession';

export interface BrandOffersAdminAuthProviderProps {
  children: ReactNode;
}

export function BrandOffersAdminAuthProvider({
  children,
}: BrandOffersAdminAuthProviderProps): JSX.Element {
  const [token, setToken] = useState<string | null>(() =>
    readBrandOffersAdminToken()
  );
  const [displayUser, setDisplayUser] = useState<string>(() =>
    readBrandOffersAdminDisplayUser()
  );

  useEffect(() => {
    const onAuthChanged = (): void => {
      setToken(readBrandOffersAdminToken());
      setDisplayUser(readBrandOffersAdminDisplayUser());
    };

    window.addEventListener(
      BRAND_OFFERS_ADMIN_AUTH_CHANGED_EVENT,
      onAuthChanged
    );
    return () =>
      window.removeEventListener(
        BRAND_OFFERS_ADMIN_AUTH_CHANGED_EVENT,
        onAuthChanged
      );
  }, []);

  const login = useCallback(async (request: BrandOffersAdminLoginRequest) => {
    const result = await apiBrandOffersAdminLogin(request);
    const label = result.username || request.username.trim();
    writeBrandOffersAdminSession({ token: result.token, displayUser: label });
    setToken(result.token);
    setDisplayUser(label);
  }, []);

  const logout = useCallback((): void => {
    clearBrandOffersAdminSession();
    notifyBrandOffersAdminAuthChanged();
  }, []);

  const value: BrandOffersAdminAuthContextType = {
    signed: Boolean(token),
    displayUser,
    login,
    logout,
  };

  return (
    <BrandOffersAdminAuthContext.Provider value={value}>
      {children}
    </BrandOffersAdminAuthContext.Provider>
  );
}
